<template>
  <v-form
      ref="form"
      :model="input"
      :action="endpoint"
      :method="method"
      @submitted="handleSubmitted"
      :validate="validate"
  >
    <template scope="{ error, errorState }">
      <b-tabs>
        <b-tab title="Message"
               active>

          <v-input-field
              label="Title"
              description="The title for the message."
              v-model="input.title"
              :error="error('title')"
              :state="errorState('title')"
              :readonly="readonly"
              placeholder="Enter Title"/>

          <v-input-field
              label="Subtitle"
              description="The subtitle for the message."
              v-model="input.subtitle"
              :state="errorState('subtitle')"
              :error="error('subtitle')"
              :readonly="readonly"
              placeholder="Enter Subtitle"/>

          <speaker-field
              label="Speaker"
              v-model="input.speaker"
              description="Add a speaker for the series."
              :state="errorState('speaker')"
              :readonly="readonly"
              :error="error('speaker')"/>

          <v-rich-text-field
              label="Description"
              description="The message description"
              :state="errorState('description')"
              v-model="input.description"
              placeholder="Enter Description."
              :readonly="readonly"
              error="error('description')"/>

          <select-field
              label="In Series?"
              description="Is it in a series, or a stand-alone message?"
              :options="seriesTypeOptions"
              @input="handleShowSeriesChange"
              :readonly="readonly"
              v-model="showSeries"/>

          <series-field
              v-if="showSeries"
              label="Series"
              description="The series the message is in."
              :state="errorState('series')"
              :error="error('series')"
              :readonly="readonly"
              v-model="input.series"/>

          <v-date-field
              label="Starts At"
              description="The date the message starts"
              v-model="input.starts_at"
              :state="errorState('starts_at')"
              :readonly="readonly"
              :error="error('starts_at')"/>

          <v-date-field
              label="Ends At"
              description="The date the message ends"
              v-model="input.ends_at"
              :state="errorState('ends_at')"
              :readonly="readonly"
              :error="error('ends_at')"/>

          <v-tags-field
              label="Tags"
              description="Search tags for the message"
              v-model="input.tags"
              :state="errorState('tags')"
              :readonly="readonly"
              :error="error('tags')"
          />
        </b-tab>

        <b-tab title="Video">
          <v-video-field
              label="Video Url"
              :state="errorState('video_url')"
              description="The URL to the Vimeo video."
              v-model="input.video_url"
              placeholder="Enter URL"
              :readonly="readonly"
              :error="error('video_url')"
          />

          <b-form-group label="Video Start Time"
                        description="Usually, the time the message starts.">
            <b-row>
              <b-col>
                <b-input-group>
                  <template #prepend>
                    <b-input-group-text>
                      <strong>Minutes</strong>
                    </b-input-group-text>
                  </template>
                  <b-form-input v-model="input.video_start_minutes"
                                :state="errorState('video_start_minutes')"
                                :readonly="readonly"
                                placeholder="MM"/>
                  <b-form-invalid-feedback v-text="error('video_start_minutes')"/>
                </b-input-group>
              </b-col>
              <b-col>
                <b-input-group>
                  <template #prepend>
                    <b-input-group-text>
                      <strong>Seconds</strong>
                    </b-input-group-text>
                  </template>
                  <b-form-input v-model="input.video_start_seconds"
                                :readonly="readonly"
                                :state="errorState('video_start_seconds')"
                                placeholder="SS"/>
                  <b-form-invalid-feedback v-text="error('video_start_seconds')"/>
                </b-input-group>
              </b-col>
            </b-row>
          </b-form-group>
        </b-tab>

        <b-tab title="Images">
          <v-image-file-field
              name="banner-image-file"
              label="Banner Image"
              description="1920px x 692px"
              :readonly="readonly"
              :state="errorState('banner_image_id')"
              :error="error('banner_image_id')"
              v-model="input.banner_image_id"/>

          <v-image-file-field
              name="square-image-file"
              label="Square Image"
              description="1024px x 1024px"
              :state="errorState('square_image_id')"
              :readonly="readonly"
              :error="error('square_image_id')"
              v-model="input.square_image_id"/>

          <v-image-file-field
              name="wide-image-file"
              label="Wide Image"
              description="1920px x 1080px"
              :state="errorState('wide_image_id')"
              :readonly="readonly"
              :error="error('wide_image_id')"
              v-model="input.wide_image_id"/>
        </b-tab>

        <b-tab title="Notes">
          <notes-field v-model="input.notes"
                       :readonly="readonly"
                       ref="notes"/>
        </b-tab>
      </b-tabs>
    </template>
    <template slot="buttons"
              v-if="this.readonly">

    </template>
  </v-form>
</template>

<script>
import SeriesField from "../Fields/SeriesField"
import SpeakerField from "../Fields/SpeakersField"
import NotesField from "../Fields/NotesField"
import SelectField from "../Fields/SelectField"

export default {
  components: {SelectField, SeriesField, SpeakerField, NotesField},
  props: ["message", "readonly"],
  data() {
    return {
      input: {
        title: null,
        subtitle: null,
        description: null,
        series: null,
        banner_image_id: null,
        square_image_id: null,
        wide_image_id: null,
        notes: [],
        tags: []
      },
      showSeries: true,
      seriesTypeOptions: [
        {
          text: 'Stand-alone message',
          value: false
        },
        {
          text: 'Part of series',
          value: true
        }
      ],
      error: ""
    }
  },
  computed: {
    exists() {
      return this.message ? true : false
    },
    endpoint() {
      if (this.exists) {
        return "/messages/" + this.message.id
      } else {
        return "/messages"
      }
    },
    method() {
      if (this.exists) {
        return "PUT"
      } else {
        return "POST"
      }
    }
  },
  created() {
    if (this.exists) {
      this.input = this.message
      if (!this.input.series || this.input.series.type === 'single') {
        this.showSeries = false
      }
    }
  },
  methods: {
    handleShowSeriesChange() {
      if (!this.showSeries) {
        this.input.series = null
      }
    },
    handleSubmitted() {
      if (!this.exists) {
        location.href = "/messages"
      }
    },
    validate() {
      if (!this.$refs.notes.validate()) {
        return "There are errors in the message notes."
      }
      if (!this.input.series) {
        let r = confirm("Are you sure you want to save this message without a series? It will be considered a stand-alone message.");
        if (r === true) {
          return true
        } else {
          return "Please add a series."
        }
      }
      return true
    }
  }
}
</script>
